import { useAuth0 } from "@auth0/auth0-react";
import {
  ApiError,
  Auth0AccountRole,
  MeetingStats,
  OrganizationResponseForAdmins,
  OrganizationsService,
  StatisticsService,
  StudentAdminResponse,
  StudentsService,
  TutorAdminResponse,
  TutorsService,
} from "client/openapi";
import { ErrorBlock, LoadingBlock } from "components/StatusBlock";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import { APIResponse, PageStatus } from "types";
import TabNavbar from "components/TabNavbar";
import { GoPerson, GoCreditCard, GoClock, GoGlobe } from "react-icons/go";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { PersonKind } from "client/openapi/models/PersonKind";
import DetailsCard from "pages/manage/ProfilePageResources/DetailsCard";
import { concatenateName } from "util/concatenateName";
import { safePhoto } from "util/safePhoto";
import TutorInfoTab from "./TutorInfoTab";
import MetricsTab from "./MetricsTab";
import "./index.css";
import TutorPaymentTab from "./TutorPaymentTab/TutorPaymentTab";
import AvailabilityTab from "./AvailabilityTab";
import BookingPageTab from "./BookingPageTab";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { RecentKind } from "client/openapi/models/RecentKind";
import { useRememberVisit } from "util/recentPeople";

export default function TutorDetail() {
  const { isLoading } = useAuth0();
  const navigate = useNavigate();
  const { currently_selected_role } = useContext(OrgRolesAndAccountContext);

  const { currently_selected_organization } = useContext(
    OrgRolesAndAccountContext
  );

  let { id: rawTutorId } = useParams();

  const [status, setStatus] = useState<PageStatus>(PageStatus.LOADING);
  const [error, setError] = useState<APIResponse>();
  const [exportingData, setExportingData] = useState<boolean>(false);
  const [exportingMeetings, setExportingMeetings] = useState<boolean>(false);
  const [tutor, setTutor] = useState<TutorAdminResponse>();
  // For the people pickers' Recent group, once the tutor is shown.
  useRememberVisit(RecentKind.TUTOR, tutor?.id);
  const [organization, setOrganization] =
    useState<OrganizationResponseForAdmins>();
  const [orgStudents, setOrgStudents] = useState<StudentAdminResponse[]>();
  const [activeTab, setActiveTab] = useState<string>("Tutor Info");
  const [meetingStats, setMeetingStats] = useState<MeetingStats>();

  const options = [
    { value: "Tutor Info", icon: GoPerson },
    { value: "Payroll", icon: GoCreditCard },
    { value: "Availability", icon: GoClock },
    { value: "Booking page", icon: GoGlobe },
  ];

  // Needs attention's "No payroll bank" opens the Payroll tab.
  useEffect(() => {
    if (window.location.hash === "#payroll") {
      setActiveTab("Payroll");
    }
  }, []);

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  function getData() {
    if (!rawTutorId) {
      setStatus(PageStatus.ERROR);
      setError({ message: "No tutor specified." });
      return;
    }

    if (isLoading) {
      return;
    }

    const tutorId = Number.parseInt(rawTutorId);

    TutorsService.getTutor({ tutorId })
      .then((t) => {
        setTutor(t);
      })
      .catch((e: ApiError) => {
        setStatus(PageStatus.ERROR);
        setError({ message: "Unable to fetch tutor details" });
        console.error(`Error (#${e.status}): ${e.message}`);
      });

    OrganizationsService.getOrganization({
      orgId: currently_selected_organization as number,
    })
      .then((o) => {
        setOrganization(o as OrganizationResponseForAdmins);
      })
      .catch((e: ApiError) => {
        setStatus(PageStatus.ERROR);
        setError({ message: "Unable to fetch organization details" });
        console.error(`Error (#${e.status}): ${e.message}`);
      });

    StatisticsService.getTutorMeetingStats({
      tutorId,
      start: moment().subtract(1, "year").toISOString(),
      until: moment().toISOString(),
      tz: moment.tz.guess(true),
    })
      .then((data) => {
        setMeetingStats(data);
      })
      .catch((e: ApiError) => {
        setStatus(PageStatus.ERROR);
        setError({ message: "Unable to fetch organization details" });
        console.error(`Error (#${e.status}): ${e.message}`);
      });

    StudentsService.getStudentsByOrganizationIfAdmin({
      orgId: currently_selected_organization as number,
    })
      .then((data) => {
        setOrgStudents(data);
      })
      .catch((e: ApiError) => {
        setStatus(PageStatus.ERROR);
        setError({ message: "Unable to fetch organization students" });
        console.error(`Error (#${e.status}): ${e.message}`);
      });
  }

  useEffect(() => {
    if (!isLoading && currently_selected_organization) {
      getData();
    }
  }, [isLoading, currently_selected_organization]);

  if (currently_selected_role !== Auth0AccountRole.ORG_ADMIN) {
    return (
      <ErrorBlock
        status={PageStatus.ERROR}
        title="Unauthorized"
        message="You do not have permission to access this page."
      />
    );
  }

  return (
    <div className="container-fluid mx-8 page--tutor-profile">
      <ErrorBlock status={status} title="Error" message={error?.message} />

      {tutor && organization ? (
        <div className="mx-5 md:mx-0">
          <button onClick={() => navigate(-1)} className="flex items-center">
            <ArrowLeftIcon className="w-6 h-6 font-bold" />
            <p className="text-lg font-bold">Back</p>
          </button>

          {/* The booking page photo, small beside the name; none without one. */}
          <div className="mt-5 flex items-center gap-4">
            {safePhoto(tutor.tutor_web_page?.photo_url) && (
              <img
                src={safePhoto(tutor.tutor_web_page?.photo_url)}
                alt={concatenateName(tutor)}
                className="h-[52px] w-[52px] flex-none rounded-full object-cover"
              />
            )}
            <p className="text-4xl font-bold">
              {tutor.first_name} {tutor.last_name}
            </p>
          </div>
          <p className="text-2xl mt-2">Tutor</p>

          <div className="my-5">
            <TabNavbar
              activeTab={activeTab}
              handleTabClick={handleTabClick}
              options={options}
            />
          </div>

          {/* Three equal columns: Details | Meeting History and Data |
              Students, Subjects and Forms. */}
          {activeTab === "Tutor Info" && orgStudents && (
            <div className="tutor-info-columns grid grid-cols-1 items-start gap-5 lg:grid-cols-3">
              <DetailsCard
                kind={PersonKind.TUTOR}
                person={tutor}
                onChange={setTutor}
              />
              <TutorInfoTab
                exportingMeetings={exportingMeetings}
                setExportingMeetings={setExportingMeetings}
                tutor={tutor}
                exportingData={exportingData}
                organization_id={currently_selected_organization as number}
                orgStudents={orgStudents}
                setStatus={setStatus}
                setError={setError}
              />
            </div>
          )}

          {activeTab === "Metrics" && meetingStats && (
            <MetricsTab tutor={tutor} meetingStats={meetingStats} />
          )}

          {activeTab === "Payroll" && (
            <div className="col-span-7">
              <TutorPaymentTab tutor={tutor} orgId={tutor.org_id} />
            </div>
          )}

          {activeTab === "Availability" && (
            <div className="col-span-7">
              <AvailabilityTab tutor={tutor} />
            </div>
          )}

          {activeTab === "Booking page" && <BookingPageTab tutor={tutor} />}
        </div>
      ) : (
        <LoadingBlock status={PageStatus.LOADING} />
      )}
    </div>
  );
}
